import React from "react";
import { useSpring, animated } from "react-spring";
import { schema, uiSchema } from "wn-claims-shared/dist/schema/vehicle-collision-schema";
import { schema as geyserSchema } from "wn-claims-shared/dist/schema/geyser-schema";
import Wizard from "../../containers/Wizard";
import { objectMap } from "../../utils/common";

var schemas = [];
var uiSchemas = [];

// Initialize schemas and uiSchemas
objectMap(schema.properties, (value) => {
  schemas.push(value);
  uiSchemas.push(uiSchema);
});

// Helper function to check if claim type and cause of loss match a specific condition
const checkCondition = (claimtypeCat, claimMotorCauseOfLoss, causedBy, conditions) => {
  return conditions.some(
    ([cause, validCauses]) => claimMotorCauseOfLoss === cause && validCauses.includes(causedBy)
  );
};

const VehicleCollision = ({ selectFormData, onBack }) => {
  const fadeIn = useSpring({ opacity: 1, from: { opacity: 0 } });
  const { claimtypeCat = "", claimMotorCauseOfLoss = "", CausedBy: causedBy = "", thirdparty = "" } = selectFormData;

  // Inject "Loss Caused by Other Parties" step when Third Party Involved = Yes
  const otherPartyIdx = schemas.findIndex((s) => s.title === "Loss Caused by Other Parties");
  if (thirdparty === "Yes") {
    if (otherPartyIdx === -1) {
      // Insert after police (index 2), before driverDetails
      schemas.splice(3, 0, geyserSchema.properties.otherParty);
      uiSchemas.splice(3, 0, {});
    }
  } else {
    if (otherPartyIdx !== -1) {
      schemas.splice(otherPartyIdx, 1);
      uiSchemas.splice(otherPartyIdx, 1);
    }
  }

  const motorClaims = [
    "Motor",
    "Motor Traders Internal",
    "Motor Traders External",
    "Motor Unspecified"
  ];

  const removeSchemas = (index, count) => {
    schemas.splice(index, count);
  };
  const removeSchemasByTitle = (titleToRemove) => {
    schemas = schemas.filter((schema) => schema.title !== titleToRemove); // Remove schema with matching title
    uiSchemas = uiSchemas.filter((_, index) => schemas[index]?.title !== titleToRemove); // Remove corresponding uiSchema
    if (
      (claimMotorCauseOfLoss === "Vehicle - Theft" && causedBy === "Vehicle - Hijack") ||
      (claimMotorCauseOfLoss === "Vehicle - Theft" && causedBy === "Vehicle - Theft") ||
      claimMotorCauseOfLoss === "Vehicle - Glass" ||
      claimMotorCauseOfLoss === "Vehicle - Roadside Assistance"
    ) {
      // Safely remove the `incidentDriverDrugTest` property if it exists
      if (schemas[0]?.properties?.incidentDriverDrugTest) {
        delete schemas[0].properties.incidentDriverDrugTest;
      }
      if (schemas[0]?.properties?.incidentCoverInfo) {
        delete schemas[0].properties.incidentCoverInfo;
      }
      if (schemas[0]?.properties?.incidentVehicleStatusInfo) {
        delete schemas[0].properties.incidentVehicleStatusInfo;
      }
    }
  };
  // Define conditions for different causes
  const conditions = [
    {
      // Police Details
      causes: [
        ["Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        [
          "Vehicle - Roadside Assistance",
          [
            "Electrical Breakdown",
            "Flat Battery / Tyre",
            "Keys locked in the vehicle",
            "Mechanical Breakdown"
          ]
        ],
        ["Vehicle - Theft", ["Vehicle - Abscondence"]],
        ["Vehicle - Glass", ["Replace", "Repair"]],
        ["Vehicle - Theft", ["Vehicle - Abscondence", "Vehicle - Hijack", "Vehicle-Theft"]]
      ],
      schemaIndex: 2,
      action: () => removeSchemasByTitle("Police")
    },
    {
      // Full Details of Driver
      causes: [
        [
          "Vehicle - Roadside Assistance",
          [
            "Electrical Breakdown",
            "Flat Battery / Tyre",
            "Keys locked in the vehicle",
            "Mechanical Breakdown"
          ]
        ],
        ["Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["Vehicle - Glass", ["Replace", "Repair"]],
        ["Vehicle - Theft", ["Vehicle - Abscondence", "Vehicle - Hijack"]],
        ["Locks & Keys", ["Locks & Keys"]],
        ["Vehicle - Theft", ["Vehicle - Abscondence", "Vehicle - Hijack", "Vehicle-Theft"]]
      ],
      schemaIndex: 3,
      action: () => removeSchemasByTitle("Full Details of Driver")
    },

    {
      //Witnesses
      causes: [
        [
          "Vehicle - Roadside Assistance",
          [
            "Electrical Breakdown",
            "Flat Battery / Tyre",
            "Keys locked in the vehicle",
            "Mechanical Breakdown"
          ]
        ],
        ["Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["Vehicle - Accident", ["Single Vehicle Accident"]],
        ["Vehicle - Glass", ["Replace", "Repair"]],
        ["Locks & Keys", ["Locks & Keys"]]
      ],
      schemaIndex: 4,
      action: () => removeSchemasByTitle("Witnesses")
    },
    {
      // Injured Passengers
      causes: [
        [
          "Vehicle - Roadside Assistance",
          [
            "Electrical Breakdown",
            "Flat Battery / Tyre",
            "Keys locked in the vehicle",
            "Mechanical Breakdown"
          ]
        ],
        ["Vehicle - Glass", ["Repair", "Replace"]],
        ["Riot & Strike", ["SASRIA", "Outside RSA / NAM", "NASRIA"]],
        ["Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["Locks & Keys", ["Locks & Keys"]],
        [
          "Vehicle - Theft",
          [
            "Equipment & Damage to vehicle",
            "Equipment & No damage to vehicle",
            "Vehicle - Abscondence",
            "Vehicle - Hijack",
            "Vehicle-Theft"
          ]
        ]
      ],
      schemaIndex: 5,
      action: () => removeSchemasByTitle("Injured Passengers")
    },
    {
      // Third Party
      causes: [
        ["Locks & Keys", ["Locks & Keys"]],
        ["Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        [
          "Vehicle - Roadside Assistance",
          [
            "Electrical Breakdown",
            "Flat Battery / Tyre",
            "Keys locked in the vehicle",
            "Mechanical Breakdown"
          ]
        ],
        ["Vehicle - Glass", ["Repair", "Replace"]],
        [
          "Vehicle - Theft",
          [
            "Equipment & Damage to vehicle",
            "Equipment & No damage to vehicle",
            "Vehicle - Abscondence",
            "Vehicle-Theft"
          ]
        ],
        ["Vehicle - Accident", ["Single Vehicle Accident"]]
      ],
      schemaIndex: 6,
      action: () => removeSchemasByTitle("Third Party")
    },
    {
      // Third Party Vehicle
      causes: [
        ["Locks & Keys", ["Locks & Keys"]],
        ["Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        [
          "Vehicle - Roadside Assistance",
          [
            "Electrical Breakdown",
            "Flat Battery / Tyre",
            "Keys locked in the vehicle",
            "Mechanical Breakdown"
          ]
        ],
        ["Vehicle - Glass", ["Repair", "Replace"]],
        [
          "Vehicle - Theft",
          [
            "Equipment & Damage to vehicle",
            "Equipment & No damage to vehicle",
            "Vehicle - Abscondence",
            "Vehicle-Theft"
          ]
        ],
        ["Vehicle - Accident", ["Single Vehicle Accident"]]
      ],
      schemaIndex: 7,
      action: () => removeSchemasByTitle("Third Party Vehicle Details")
    },
    {
      //Third Party Injured Persons
      causes: [
        [
          "Vehicle - Roadside Assistance",
          [
            "Electrical Breakdown",
            "Flat Battery / Tyre",
            "Keys locked in the vehicle",
            "Mechanical Breakdown"
          ]
        ],
        ["Malicious Damage", ["Malicious Damage", "Malicious Damage With Theft"]],
        ["Vehicle - Glass", ["Repair", "Replace"]],
        ["Earthquake", ["Mining Operations", "Natural Phenomenon"]],
        ["Storm", ["Hail / Snow", "Lightning", "Storm / Wind", "Water / Flood"]],
        ["Vehicle - Accident", ["Single Vehicle Accident"]],
        [
          "Vehicle - Theft",
          ["Vehicle-Theft", "Equipment & No damage to vehicle", "Vehicle - Abscondence"]
        ],
        ["Locks & Keys", ["Locks & Keys"]]
      ],
      schemaIndex: 8,
      action: () => removeSchemasByTitle("Third Party Injured Persons")
    }

    // Add more conditions as needed
  ];

  motorClaims.forEach((type) => {
    if (claimtypeCat === type) {
      conditions.forEach((condition) => {
        if (checkCondition(claimtypeCat, claimMotorCauseOfLoss, causedBy, condition.causes)) {
          condition.action();
        }
      });
    }
  });

  return (
    <animated.div style={fadeIn}>
      <h1>{schema.title}</h1>
      <h5>{schema.description}</h5>
      <Wizard schemas={schemas} uiSchemas={uiSchemas} selectFormData={selectFormData} onBack={onBack} />
    </animated.div>
  );
};

export default VehicleCollision;
